import { createContext, useContext, useState, useEffect, useRef, useCallback, type ReactNode } from 'react';
import {
  pb,
  currentSession,
  pbErrorMessage,
  USERS_COLLECTION,
  type Session,
  getMfaSession,
  setMfaSession,
  clearMfaSession,
} from '../services/pocketbase';
import { rememberAuthMethod } from '../utils/authMethod';
import type { UserProfile, Organization, PlanFeatures, PlanType, AnalysisType } from '../../shared/types';
import { PLAN_FEATURES, BUNDLES, isSubscriptionPaymentUnconfirmed } from '../../shared/types';

interface AuthContextType {
  user: UserProfile | null;
  organization: Organization | null;
  features: PlanFeatures;
  session: Session | null;
  isLoading: boolean;
  /** True when the PocketBase token is valid but the account demands a TOTP code the server hasn't seen yet (ex-`aal1 → aal2`). */
  mfaRequired: boolean;
  /** Super admin only: simulate a plan for UI preview (null = real view) */
  previewPlan: PlanType | null;
  setPreviewPlan: (plan: PlanType | null) => void;
  /** Super admin only: simulate the post-signup state (no sub, no credits, no choice made yet) */
  previewOnboarding: boolean;
  setPreviewOnboarding: (value: boolean) => void;
  /** Super admin only: simulate a "bought Pack Lancement bundle" profile (limited per-type credits). */
  previewBundle: boolean;
  setPreviewBundle: (value: boolean) => void;
  /** Decrement a simulated bundle credit (preview only) — lets the admin see the limit shrink in real time. */
  consumePreviewCredit: (type: AnalysisType) => void;
  /** True when this org is a fresh signup that hasn't subscribed nor bought a bundle. */
  isOnboarding: boolean;
  /** True after the user follows a password-recovery link (PocketBase reset token in the URL). Gates the app to a "set new password" view. */
  recoveryMode: boolean;
  /** Last Google OAuth failure — the popup flow can't return an error through `signInWithGoogle`'s void signature. */
  oauthError: string | null;
  signIn: (email: string, password: string) => Promise<{ error?: string; requiresMfa?: boolean }>;
  signInWithGoogle: () => Promise<void>;
  signUp: (
    email: string,
    password: string,
    termsAccepted: { version: string; acceptedAt: string },
    /** Code promo lu sur le QR — mémorisé sur le compte pour survivre à
     *  l'ouverture de session dans un autre navigateur. */
    promoCode?: string,
    /** Métier et secteur choisis dans le formulaire (listes de shared/types). */
    profile?: { jobTitle: string; industry: string },
  ) => Promise<{ error?: string }>;
  /** Envoie un code à 6 chiffres par email (inscription OU reconnexion).
   *  Parcours des cartes de visite salon : pas de mot de passe à inventer. */
  signInWithOtp: (
    email: string,
    opts: { termsAccepted: { version: string; acceptedAt: string }; promoCode?: string },
  ) => Promise<{ error?: string }>;
  /** Valide le code à 6 chiffres reçu par email et ouvre la session. */
  verifyEmailOtp: (email: string, token: string) => Promise<{ error?: string }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error?: string }>;
  /** Set a new password for the user currently in recovery mode, then clear the gate. */
  updatePassword: (newPassword: string) => Promise<{ error?: string }>;
  refreshProfile: () => Promise<void>;
  /** Verify the 6-digit TOTP code of the authenticator app and unlock the session. */
  verifyMfaChallenge: (code: string) => Promise<{ error?: string }>;
}

/* ── Trappe de dev — pendant client de AUTH_ENABLED=false ──────────────
   (server/middleware/auth.ts). Sans PocketBase joignable, aucune session ne
   peut s'ouvrir et l'application reste bloquée sur l'écran de connexion. En
   dev UNIQUEMENT, VITE_AUTH_ENABLED=false saute PocketBase : le profil
   synthétique servi par /api/auth/me devient l'utilisateur courant
   (`npm run dev:noauth` pose les deux variables). Le garde `import.meta.env.DEV`
   retire ce chemin des builds de production. */
const DEV_AUTH_BYPASS = import.meta.env.DEV && import.meta.env.VITE_AUTH_ENABLED === 'false';

const AuthContext = createContext<AuthContextType | null>(null);

export function useAuth(): AuthContextType {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

interface MeResponse extends UserProfile {
  organization: Organization | null;
}

const API_URL = import.meta.env.VITE_API_URL ?? '';

type MeResult =
  | { kind: 'ok'; me: MeResponse }
  | { kind: 'mfa_required' }
  | { kind: 'none' };

/**
 * Charge le profil. Un 403 `mfa_required` n'est PAS une absence de session :
 * c'est le serveur qui réclame le code de l'application d'authentification
 * avant de servir quoi que ce soit (cf. requireAuth côté Express).
 */
async function fetchMe(accessToken: string): Promise<MeResult> {
  try {
    const headers: Record<string, string> = { Authorization: `Bearer ${accessToken}` };
    const mfa = getMfaSession();
    if (mfa) headers['X-Evinly-MFA-Session'] = mfa;
    const res = await fetch(`${API_URL}/api/auth/me`, { headers });
    if (res.status === 403) {
      const body = await res.json().catch(() => ({}));
      if (body?.error === 'mfa_required') return { kind: 'mfa_required' };
      return { kind: 'none' };
    }
    if (!res.ok) return { kind: 'none' };
    return { kind: 'ok', me: await res.json() };
  } catch {
    return { kind: 'none' };
  }
}

/**
 * Récupère le jeton de réinitialisation de mot de passe présent dans l'URL.
 *
 * Différence majeure avec Supabase : le lien de reset Supabase ouvrait une
 * VRAIE session (hash `#access_token=…`), d'où l'événement `PASSWORD_RECOVERY`.
 * Le lien PocketBase ne contient qu'un jeton à usage unique qui n'authentifie
 * PAS — il ne sert qu'à `confirmPasswordReset()`. Le « mode récupération » est
 * donc simplement « un jeton de reset est en main ».
 *
 * Deux formes acceptées :
 *   • `/reset-password?token=…`  → modèle d'email versionné (recommandé)
 *   • `…#/auth/confirm-password-reset/…` → modèle PocketBase par défaut
 *
 * Le jeton est retiré de l'URL immédiatement pour ne pas rester dans
 * l'historique du navigateur. Appelée UNE SEULE FOIS au niveau module : sous
 * React 19 StrictMode, un `useState(initializer)` serait ré-exécuté et le
 * second appel — l'URL ayant déjà été nettoyée — écraserait le jeton par `null`.
 */
function readRecoveryToken(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const url = new URL(window.location.href);
    const fromHash = url.hash.match(/confirm-password-reset\/([^/?#]+)/)?.[1] ?? null;
    const fromQuery = url.pathname.startsWith('/reset-password')
      ? url.searchParams.get('token')
      : null;
    const token = fromQuery ?? fromHash;
    if (!token) return null;

    url.searchParams.delete('token');
    window.history.replaceState({}, '', `${url.pathname}${url.search}`);
    return token;
  } catch {
    return null;
  }
}

const INITIAL_RECOVERY_TOKEN = readRecoveryToken();

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<UserProfile | null>(null);
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [previewPlan, setPreviewPlanState] = useState<PlanType | null>(null);
  const [previewOnboarding, setPreviewOnboardingState] = useState(false);
  // Crédits simulés d'un acheteur de bundle (preview admin). null = mode inactif.
  const [previewBundleCredits, setPreviewBundleCredits] = useState<Partial<Record<AnalysisType, number>> | null>(null);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [oauthError, setOauthError] = useState<string | null>(null);
  const [recoveryToken, setRecoveryToken] = useState<string | null>(INITIAL_RECOVERY_TOKEN);

  // Défi OTP email en cours (parcours salon) : l'`otpId` rendu par le serveur
  // au démarrage. Vit dans une ref : il ne pilote aucun rendu.
  const otpPendingRef = useRef<{ email: string; otpId: string } | null>(null);
  const bootstrappedRef = useRef(false);

  const recoveryMode = recoveryToken !== null;
  const previewBundle = previewBundleCredits !== null;

  // Modes de preview mutuellement exclusifs : activer l'un désactive les autres.
  const setPreviewPlan = useCallback((plan: PlanType | null) => {
    setPreviewPlanState(plan);
    if (plan !== null) { setPreviewOnboardingState(false); setPreviewBundleCredits(null); }
  }, []);
  const setPreviewOnboarding = useCallback((value: boolean) => {
    setPreviewOnboardingState(value);
    if (value) { setPreviewPlanState(null); setPreviewBundleCredits(null); }
  }, []);
  const setPreviewBundle = useCallback((value: boolean) => {
    if (value) {
      const pack = BUNDLES.find((b) => b.id === 'pack_lancement');
      const credits: Partial<Record<AnalysisType, number>> = {};
      pack?.creditLines.forEach((l) => {
        if (!l.analysisType) return;
        credits[l.analysisType] = (credits[l.analysisType] ?? 0) + l.credits;
      });
      setPreviewBundleCredits(credits);
      setPreviewPlanState(null);
      setPreviewOnboardingState(false);
    } else {
      setPreviewBundleCredits(null);
    }
  }, []);
  const consumePreviewCredit = useCallback((type: AnalysisType) => {
    setPreviewBundleCredits((prev) => {
      if (!prev) return prev;
      const cur = prev[type] ?? 0;
      if (cur <= 0) return prev;
      return { ...prev, [type]: cur - 1 };
    });
  }, []);

  // Super admin: use the plan from /api/auth/me (backend overrides to premium)
  // Regular user: use the org plan
  // Preview mode: override with simulated plan (un acheteur de bundle = plan 'free' + crédits)
  const realPlan = user?.plan ?? organization?.plan ?? 'free';
  const effectivePlan = previewBundle ? 'free' : (previewPlan ?? realPlan);

  // Solde de crédits effectif (preview bundle = somme simulée, sinon réel).
  const bundleBalance = previewBundleCredits
    ? Object.values(previewBundleCredits).reduce((a, b) => a + (b ?? 0), 0)
    : 0;
  const creditBalanceEff = previewBundle ? bundleBalance : (organization?.creditBalance ?? 0);

  // Override apiAccess with the per-org gate. The plan provides the
  // ceiling (only Premium+ are eligible at all), the org flag is the
  // actual switch — both must be true. In preview mode we ignore the
  // org flag and trust the simulated plan, so super-admins can still
  // see what the API tab looks like for any plan.
  // Le super admin, lui, garde l'onglet API dans TOUS les modes (preview
  // compris) : le serveur lui ouvre la gestion des clés sans condition.
  const planFeatures = (previewPlan || previewBundle)
    ? PLAN_FEATURES[effectivePlan]
    : {
        ...PLAN_FEATURES[effectivePlan],
        apiAccess: PLAN_FEATURES[effectivePlan].apiAccess && organization?.apiAccessEnabled === true,
      };
  const baseFeatures = user?.isSuperAdmin
    ? { ...planFeatures, apiAccess: true }
    : planFeatures;
  // Un acheteur de crédits/bundle (plan free + crédits) N'EST PAS en lecture
  // seule : il doit pouvoir copier, partager et conserver ses analyses. On
  // débloque donc ces features dès qu'il reste des crédits (réel ou preview).
  const features = creditBalanceEff > 0
    ? { ...baseFeatures, copyText: true, history: true }
    : baseFeatures;

  // When previewing a plan or a bundle, simulate the org's limits to match
  const effectiveOrganization = (previewBundle && organization)
    ? {
        ...organization,
        plan: 'free' as PlanType,
        subscriptionStatus: null,
        freeAnalysesLimit: 0,
        freeAnalysesUsed: 0,
        creditBalance: bundleBalance,
        creditsPerType: previewBundleCredits ?? {},
      }
    : (previewPlan && organization)
    ? {
        ...organization,
        plan: previewPlan,
        freeAnalysesLimit: features.monthlyLimit ?? 0,
        freeAnalysesUsed: 0,
      }
    : organization;

  // Real onboarding state = no active subscription AND no credits.
  // We exclude super admins (who always have premium overridden by the backend)
  // so they can preview the app normally without the onboarding gate triggering.
  const realIsOnboarding = !!(
    organization &&
    !user?.isSuperAdmin &&
    // Un abonnement dont le premier paiement n'est pas confirmé (`incomplete`)
    // n'accorde aucun plan : l'org reste dans le parcours d'accueil.
    (!organization.subscriptionStatus || isSubscriptionPaymentUnconfirmed(organization.subscriptionStatus)) &&
    organization.creditBalance === 0 &&
    organization.plan !== 'pro' &&
    organization.plan !== 'premium' &&
    organization.plan !== 'enterprise' &&
    organization.plan !== 'beta' &&
    // `partner` = accès illimité offert : c'est un compte actif, pas un signup.
    organization.plan !== 'partner'
  );
  const isOnboarding = previewOnboarding || realIsOnboarding;

  const applyMe = useCallback((result: MeResult) => {
    if (result.kind === 'ok') {
      const { organization: org, ...profile } = result.me;
      setUser(profile);
      setOrganization(org);
      setMfaRequired(false);
    } else if (result.kind === 'mfa_required') {
      setUser(null);
      setOrganization(null);
      setMfaRequired(true);
    } else {
      setUser(null);
      setOrganization(null);
      setMfaRequired(false);
    }
  }, []);

  const loadProfile = useCallback(async (s: Session | null) => {
    // Trappe de dev : aucune session, le serveur ignore le jeton.
    const token = s?.token ?? (DEV_AUTH_BYPASS ? 'dev-bypass' : null);
    applyMe(token ? await fetchMe(token) : { kind: 'none' });
  }, [applyMe]);

  // ── Initialisation + écoute des changements de session ────────────────
  //
  // `pb.authStore.onChange(cb, true)` remplace `supabase.auth.onAuthStateChange` :
  // avec `fireImmediately = true`, le callback part une première fois avec l'état
  // restauré depuis localStorage — l'équivalent exact de `INITIAL_SESSION`.
  //
  // Le contournement `setTimeout(…, 0)` de Supabase n'a plus lieu d'être : il
  // servait à sortir du verrou asynchrone interne de supabase-js, que
  // PocketBase n'a pas. On garde en revanche un compteur de génération, parce
  // que StrictMode monte l'effet deux fois et que deux `GET /api/auth/me`
  // peuvent revenir dans le désordre.
  useEffect(() => {
    let mounted = true;
    let generation = 0;

    if (DEV_AUTH_BYPASS) {
      void loadProfile(null).finally(() => { if (mounted) setIsLoading(false); });
      return () => { mounted = false; };
    }

    const unsubscribe = pb.authStore.onChange(() => {
      if (!mounted) return;
      const gen = ++generation;
      const s = currentSession();
      setSession(s);

      void (async () => {
        const result: MeResult = s?.token ? await fetchMe(s.token) : { kind: 'none' };
        // Une réponse périmée (une session plus récente est arrivée entre-temps)
        // ne doit jamais écraser le profil courant.
        if (!mounted || gen !== generation) return;
        applyMe(result);
        setIsLoading(false);
      })();
    }, true);

    return () => {
      mounted = false;
      unsubscribe();
    };
  }, [applyMe, loadProfile]);

  // Revalidation du jeton restauré depuis localStorage.
  //
  // `authStore.isValid` ne fait que vérifier localement la claim `exp` : il ne
  // détecte ni un compte supprimé, ni un mot de passe changé (qui invalide les
  // jetons côté PocketBase). Le SDK ne rafraîchit pas le jeton tout seul —
  // `authRefresh()` valide auprès du serveur ET prolonge le jeton. La session
  // MFA (secret à part, cf. services/pocketbase.ts) ne dépend pas du jeton :
  // un rechargement de page ne redemande pas le code.
  useEffect(() => {
    if (bootstrappedRef.current || DEV_AUTH_BYPASS) return;
    bootstrappedRef.current = true;
    if (!pb.authStore.isValid) return;
    pb.collection(USERS_COLLECTION)
      .authRefresh()
      .catch(() => {
        pb.authStore.clear();
        clearMfaSession();
      });
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    setOauthError(null);
    try {
      await pb.collection(USERS_COLLECTION).authWithPassword(email, password);
    } catch (err) {
      return { error: pbErrorMessage(err, 'Email ou mot de passe incorrect.') };
    }
    rememberAuthMethod('password');
    // `authStore.onChange` a déjà propagé la session. On recharge tout de même
    // le profil ici pour que l'appelant puisse enchaîner : un 403 `mfa_required`
    // bascule sur l'écran de saisie du code (AuthGate).
    const s = currentSession();
    setSession(s);
    const result = s?.token ? await fetchMe(s.token) : { kind: 'none' as const };
    applyMe(result);
    if (result.kind === 'mfa_required') return { requiresMfa: true };
    return {};
  }, [applyMe]);

  const verifyMfaChallenge = useCallback(async (code: string) => {
    const s = currentSession();
    if (!s?.token) {
      return { error: 'Session expirée. Reconnectez-vous.' };
    }
    try {
      const res = await fetch(`${API_URL}/api/auth/mfa/challenge`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${s.token}` },
        body: JSON.stringify({ code }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        return { error: body?.message ?? 'Code invalide.' };
      }
      const body = (await res.json().catch(() => ({}))) as { mfaSession?: string };
      if (!body.mfaSession) return { error: 'Réponse inattendue du serveur. Réessayez.' };
      setMfaSession(body.mfaSession);
    } catch {
      return { error: 'Serveur injoignable. Réessayez dans un instant.' };
    }
    // La session MFA est ouverte côté serveur : le profil se charge.
    await loadProfile(s);
    return {};
  }, [loadProfile]);

  // OAuth2 « tout-en-un » du SDK : ouverture d'une popup vers Google, puis
  // résolution via le canal temps réel de PocketBase. L'onglet applicatif reste
  // intact, là où Supabase faisait une redirection pleine page. L'échec
  // éventuel — popup bloquée, fenêtre fermée — est exposé via `oauthError`.
  const signInWithGoogle = useCallback(async () => {
    setOauthError(null);
    try {
      await pb.collection(USERS_COLLECTION).authWithOAuth2({ provider: 'google' });
      rememberAuthMethod('google');
      const s = currentSession();
      setSession(s);
      await loadProfile(s);
    } catch (err) {
      setOauthError(pbErrorMessage(err, 'Connexion Google annulée ou impossible.'));
    }
  }, [loadProfile]);

  const signUp = useCallback(async (
    email: string,
    password: string,
    termsAccepted: { version: string; acceptedAt: string },
    promoCode?: string,
    profile?: { jobTitle: string; industry: string },
  ) => {
    // La collection `users` est en `createRule: null` : impossible de créer un
    // compte depuis le navigateur. L'inscription passe par Express, qui crée
    // l'enregistrement, horodate l'acceptation des CGU et déclenche l'email
    // de vérification. `termsAccepted` n'est pas transmis : la preuve de
    // consentement est établie par le serveur, à l'instant de la création.
    void termsAccepted;
    try {
      const res = await fetch(`${API_URL}/api/auth/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          password,
          promoCode: promoCode || undefined,
          jobTitle: profile?.jobTitle || undefined,
          industry: profile?.industry || undefined,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        return { error: data.message ?? data.error ?? `Inscription impossible (erreur ${res.status}).` };
      }
      return {};
    } catch {
      return { error: "Serveur d'inscription injoignable. Réessayez dans un instant." };
    }
  }, []);

  // ── Inscription / connexion par code à 6 chiffres (OTP) ──
  //
  // Parcours des cartes de visite salon : le visiteur scanne, tape son email,
  // reçoit un code, le saisit — sans quitter la page. Le serveur crée le compte
  // s'il n'existe pas (CGU horodatées, code promo mémorisé) puis demande le
  // code à PocketBase ; la validation, elle, se fait directement contre
  // PocketBase, qui marque alors l'adresse comme vérifiée.
  const signInWithOtp = useCallback(async (
    email: string,
    opts: { termsAccepted: { version: string; acceptedAt: string }; promoCode?: string },
  ) => {
    void opts.termsAccepted;
    try {
      const res = await fetch(`${API_URL}/api/auth/otp/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, promoCode: opts.promoCode || undefined }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        return { error: data.message ?? data.error ?? `Envoi impossible (erreur ${res.status}).` };
      }
      otpPendingRef.current = data.otpId ? { email, otpId: data.otpId } : null;
      return {};
    } catch {
      return { error: "Serveur injoignable. Réessayez dans un instant." };
    }
  }, []);

  const verifyEmailOtp = useCallback(async (email: string, token: string) => {
    const pending = otpPendingRef.current;
    if (!pending || pending.email !== email) {
      return { error: 'Code expiré. Demandez un nouveau code.' };
    }
    try {
      await pb.collection(USERS_COLLECTION).authWithOTP(pending.otpId, token.replace(/\s+/g, ''));
    } catch (err) {
      return { error: pbErrorMessage(err, 'Code invalide ou expiré.') };
    }
    otpPendingRef.current = null;
    rememberAuthMethod('otp');
    // authStore.onChange prend le relais pour charger le profil.
    return {};
  }, []);

  const signOut = useCallback(async () => {
    // PocketBase est sans session serveur : « se déconnecter » = jeter le jeton.
    // `clear()` déclenche `authStore.onChange`, qui remet session/profil à null.
    pb.authStore.clear();
    clearMfaSession();
    setSession(null);
    setUser(null);
    setOrganization(null);
    setPreviewPlanState(null);
    setPreviewOnboardingState(false);
    setPreviewBundleCredits(null);
    setMfaRequired(false);
    setOauthError(null);
    otpPendingRef.current = null;
  }, []);

  const resetPassword = useCallback(async (email: string) => {
    try {
      await pb.collection(USERS_COLLECTION).requestPasswordReset(email);
      return {};
    } catch (err) {
      return { error: pbErrorMessage(err, "Impossible d'envoyer l'email de réinitialisation.") };
    }
  }, []);

  const updatePassword = useCallback(async (newPassword: string) => {
    if (!recoveryToken) {
      return { error: 'Lien de réinitialisation absent ou expiré. Redemandez un email.' };
    }
    try {
      await pb
        .collection(USERS_COLLECTION)
        .confirmPasswordReset(recoveryToken, newPassword, newPassword);
    } catch (err) {
      return {
        error: pbErrorMessage(err, 'Lien expiré ou mot de passe refusé. Redemandez un email de réinitialisation.'),
      };
    }

    // Changer le mot de passe invalide TOUS les jetons existants côté
    // PocketBase (le `tokenKey` de l'enregistrement est régénéré). L'utilisateur
    // se reconnecte avec son nouveau mot de passe.
    pb.authStore.clear();
    clearMfaSession();
    setRecoveryToken(null);
    return {};
  }, [recoveryToken]);

  const refreshProfile = useCallback(async () => {
    await loadProfile(currentSession());
  }, [loadProfile]);

  return (
    <AuthContext.Provider value={{
      user, organization: effectiveOrganization, features, session, isLoading,
      mfaRequired,
      previewPlan, setPreviewPlan,
      previewOnboarding, setPreviewOnboarding,
      previewBundle, setPreviewBundle, consumePreviewCredit,
      isOnboarding,
      recoveryMode,
      oauthError,
      signIn, signInWithGoogle, signUp, signInWithOtp, verifyEmailOtp,
      signOut, resetPassword, updatePassword, refreshProfile,
      verifyMfaChallenge,
    }}>
      {children}
    </AuthContext.Provider>
  );
}
